import { useState } from "react";
import { useAIDecisions } from "@app/hooks/useAIDecisions";
import { ExpandedAIAnalysis } from "./ExpandedAIAnalysis";
import { useTradingSystemPayload } from "@app/hooks/useTradingSystemPayload";
import type { ActivePosition } from "@shared/types";
import "./aiDecisionBadge.css";

interface AIDecisionBadgeProps {
  position: ActivePosition;
}

/**
 * AI Decision Badge Component
 * 
 * Displays current AI decision status for a position with expandable details.
 * 
 * Features:
 * - Shows current decision (HOLD/EXIT/ADJUST) with confidence
 * - Click to expand/collapse detailed analysis
 * - Auto-refreshes every 30 seconds (only for active positions)
 * - Graceful error handling
 * - Checks if position exists in active positions before fetching
 */
export function AIDecisionBadge({ position }: AIDecisionBadgeProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const { data: payload } = useTradingSystemPayload();
  
  // SUSTAINABLE SOLUTION: Check if position exists in active positions before fetching AI decisions
  // This prevents unnecessary API calls for positions that were removed
  const positionExists = payload?.active_positions?.some((p) => p.id === position.id) ?? true;
  
  const { data, isLoading, error } = useAIDecisions(
    position.id,
    positionExists, // Only enable query if position exists in active positions
    30000
  );

  const handleBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const getDecisionDisplay = () => {
    if (isLoading) {
      return { text: "Loading...", color: "neutral", icon: "⏳" };
    }

    if (error || !data?.current_decision) {
      return { text: "N/A", color: "neutral", icon: "—" };
    }

    const decision = data.current_decision;
    const action = decision.action?.toUpperCase() || "HOLD";
    const confidence = decision.confidence ? Math.round(decision.confidence) : null;

    let color: "hold" | "adjust" | "exit" | "neutral" = "neutral";
    let icon = "🤖";

    switch (action) {
      case "HOLD":
        color = "hold";
        icon = "🟢";
        break;
      case "EXIT":
      case "PARTIAL_EXIT":
        color = "exit";
        icon = "🔴";
        break;
      case "ADJUST_SL":
      case "ADJUST_TP":
      case "ADJUST":
        color = "adjust";
        icon = "🟡";
        break;
      default:
        color = "neutral";
        icon = "🤖";
    }

    const text = confidence !== null ? `${action} ${confidence}%` : action;

    return { text, color, icon };
  };

  const display = getDecisionDisplay();

  return (
    <div className="ai-decision-badge-container">
      <div
        className={`ai-decision-badge ai-decision-badge--${display.color} ${expanded ? "ai-decision-badge--expanded" : ""}`}
        onClick={handleBadgeClick}
        title="Click to view AI analysis details"
      >
        <span className="ai-decision-badge__icon">{display.icon}</span>
        <span className="ai-decision-badge__text">{display.text}</span>
        <span className={`ai-decision-badge__arrow ${expanded ? "ai-decision-badge__arrow--expanded" : ""}`}>
          ▼
        </span>
      </div>
    </div>
  );
}

interface AIDecisionBadgeWithExpandedProps {
  position: ActivePosition;
  expanded: boolean;
  onToggle: () => void;
}

/**
 * AI Decision Badge Component with external expanded state management.
 * 
 * Used when expanded state is managed by parent component (e.g., in table rows).
 */
export function AIDecisionBadgeWithExpanded({
  position,
  expanded,
  onToggle,
}: AIDecisionBadgeWithExpandedProps): JSX.Element {
  const { data, isLoading, error } = useAIDecisions(position.id, true, 30000);

  const handleBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle();
  };

  const getDecisionDisplay = () => {
    if (isLoading) {
      return { text: "Loading...", color: "neutral", icon: "⏳" };
    }

    if (error || !data?.current_decision) {
      return { text: "N/A", color: "neutral", icon: "—" };
    }

    const decision = data.current_decision;
    const action = decision.action?.toUpperCase() || "HOLD";
    const confidence = decision.confidence ? Math.round(decision.confidence) : null;

    let color: "hold" | "adjust" | "exit" | "neutral" = "neutral";
    let icon = "🤖";

    switch (action) {
      case "HOLD":
        color = "hold";
        icon = "🟢";
        break;
      case "EXIT":
      case "PARTIAL_EXIT":
        color = "exit";
        icon = "🔴";
        break;
      case "ADJUST_SL":
      case "ADJUST_TP":
      case "ADJUST":
        color = "adjust";
        icon = "🟡";
        break;
      default:
        color = "neutral";
        icon = "🤖";
    }

    const text = confidence !== null ? `${action} ${confidence}%` : action;

    return { text, color, icon };
  };

  const display = getDecisionDisplay();

  return (
    <div className="ai-decision-badge-container">
      <div
        className={`ai-decision-badge ai-decision-badge--${display.color} ${expanded ? "ai-decision-badge--expanded" : ""}`}
        onClick={handleBadgeClick}
        title="Click to view AI analysis details"
      >
        <span className="ai-decision-badge__icon">{display.icon}</span>
        <span className="ai-decision-badge__text">{display.text}</span>
        <span className={`ai-decision-badge__arrow ${expanded ? "ai-decision-badge__arrow--expanded" : ""}`}>
          ▼
        </span>
      </div>
    </div>
  );
}

